<template>
  <b-sidebar
    id="add-new-user-sidebar"
    :visible="isAddNewUserSidebarActive"
    bg-variant="white"
    sidebar-class="sidebar-lg"
    shadow
    backdrop
    no-header
    right
    @hidden="resetForm"
    @change="(val) => $emit('update:is-add-new-user-sidebar-active', val)"
  >
    <template #default="{ hide }">
      <!-- Header -->
      <div class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1">
        <h5 class="mb-0">
          {{ countryData.id ? 'Editar' : 'Nuevo' }} País
        </h5>

        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="hide"
        />

      </div>

      <!-- BODY -->
      <validation-observer
        #default="{ handleSubmit }"
        ref="refFormObserver"
      >
        <!-- Form -->
        <b-form
          class="p-2"
          @submit.prevent="handleSubmit(onSubmit)"
          @reset.prevent="resetForm"
        >

          <!-- ISO -->
          <validation-provider
            #default="validationContext"
            name="ISO"
            rules="required"
          >
            <b-form-group
              label="ISO"
              label-for="iso"
            >
              <b-form-input
                id="iso"
                v-model="countryData.iso"
                autofocus
                :state="getValidationState(validationContext)"
                trim
                class="text-uppercase"
              />

              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Name -->
          <validation-provider
            #default="validationContext"
            name="País"
            rules="required"
          >
            <b-form-group
              label="País"
              label-for="name"
            >
              <b-form-input
                id="name"
                v-model="countryData.name"
                autofocus
                :state="getValidationState(validationContext)"
                trim
              />

              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Lang -->
          <validation-provider
            #default="validationContext"
            name="Idioma"
            rules="required"
          >
            <b-form-group
              label="Idioma"
              label-for="lang"
              :state="getValidationState(validationContext)"
            >
              <v-select
                v-model="countryData.langs"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="languages"
                :clearable="false"
                multiple
                :close-on-select="false"
                input-id="lang"
              />
              <b-form-invalid-feedback :state="getValidationState(validationContext)">
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Status -->
          <validation-provider
            name="Activo"
          >
            <b-form-group
              label="Activo"
              label-for="statusCheck"
            >
              <b-form-checkbox
                id="statusCheck"
                v-model="countryData.status"
                name="statusCheck"
              >
                {{ countryData.status ? 'Si' : 'No' }}
              </b-form-checkbox>
            </b-form-group>
          </validation-provider>

          <!-- Locked -->
          <validation-provider
            name="Bloqueado"
          >
            <b-form-group
              label="Bloqueado"
              label-for="lockedCheck"
            >
              <b-form-checkbox
                id="lockedCheck"
                v-model="countryData.locked"
                name="lockedCheck"
              >
                {{ countryData.locked ? 'Si' : 'No' }}
              </b-form-checkbox>
            </b-form-group>
          </validation-provider>

          <!-- Form Actions -->
          <div class="d-flex mt-2">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              :disabled="loading"
              variant="primary"
              class="mr-2"
              type="submit"
            >
              <template v-if="loading">
                <b-spinner
                  variant="light"
                  class="mr-75"
                  small
                />
                <span>{{ countryData.id ? 'Actualizando' : 'Creando' }}</span>
              </template>
              <span v-else>{{ countryData.id ? 'Actualizar' : 'Crear' }}</span>
            </b-button>
            <b-button
              v-ripple.400="'rgba(186, 191, 199, 0.15)'"
              :disabled="loading"
              type="button"
              variant="outline-secondary"
              @click="hide"
            >
              Cancelar
            </b-button>
          </div>

        </b-form>
      </validation-observer>
    </template>
  </b-sidebar>
</template>

<script>
import {
  BSidebar, BForm, BFormGroup, BFormInput, BFormInvalidFeedback, BButton, BFormCheckbox, BSpinner,
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { ref, onMounted } from '@vue/composition-api'
import { required, alphaNum, email } from '@validations'
import formValidation from '@core/comp-functions/forms/form-validation'
import Ripple from 'vue-ripple-directive'
import vSelect from 'vue-select'
import countriesList from '@/@fake-db/data/other/countries'
// import store from '@/store'
import useCountries from './useCountries'
import useLangs from '../langs/useLangs'

export default {
  components: {
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BButton,
    BFormCheckbox,
    BSpinner,
    vSelect,

    // Form Validation
    ValidationProvider,
    ValidationObserver,
  },
  directives: {
    Ripple,
  },
  model: {
    prop: 'isAddNewUserSidebarActive',
    event: 'update:is-add-new-user-sidebar-active',
  },
  props: {
    isAddNewUserSidebarActive: {
      type: Boolean,
      required: true,
    },
    item: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      required,
      alphaNum,
      email,
      countriesList,
    }
  },
  watch: {
    isAddNewUserSidebarActive(val) {
      if (val && this.item) {
        console.log(this.item)
        this.countryData = {
          id: this.item.id,
          iso: this.item.iso,
          name: this.item.name,
          langs: this.item.langs?.map(lang => ({
            id: lang.id,
            label: lang.name,
            value: lang.iso,
          })),
          status: this.item.status,
          locked: this.item.locked,
        }
      } else {
        this.countryData = {
          id: null,
          iso: '',
          name: '',
          langs: [],
          status: true,
          locked: false,
        }
        this.$emit('update:item', null)
      }
    },
  },
  setup(_, { emit }) {
    const blankCountryData = {
      id: null,
      iso: '',
      name: '',
      langs: [],
      status: true,
      locked: false,
    }

    const countryData = ref(JSON.parse(JSON.stringify(blankCountryData)))
    const resetCountryData = () => {
      countryData.value = JSON.parse(JSON.stringify(blankCountryData))
    }

    const loading = ref(false)
    const { countryStore, countryUpdate } = useCountries()
    const { fetchLangsSelector } = useLangs()
    const languages = ref([])

    onMounted(async () => {
      const list = await fetchLangsSelector(true)

      languages.value = list.map(l => ({ ...l, value: l.id }))
    })

    const onSubmit = async () => {
      loading.value = true

      const data = { ...countryData.value }
      data.iso = data.iso.toUpperCase()
      data.langs = Array.isArray(data.langs) ? data.langs.map(i => i.id) : data.langs.id

      try {
        if (data.id) {
          await countryUpdate(data.id, data)
        } else {
          await countryStore(data)
        }

        emit('refetchData')
        emit('update:is-add-new-user-sidebar-active', false)
      } catch (error) {
        console.log(error)
      } finally {
        loading.value = false
      }
    }

    const {
      refFormObserver,
      getValidationState,
      resetForm,
    } = formValidation(resetCountryData)

    return {
      countryData,
      languages,
      loading,
      onSubmit,

      refFormObserver,
      getValidationState,
      resetForm,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

#add-new-user-sidebar {
  .vs__dropdown-menu {
    max-height: 200px !important;
  }
}
</style>
